<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("orders_new.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right"></div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-12">
                            <h3 class="mb-0">{{ $t("orders_new.title") }}</h3>
                        </div>
                    </div>
                </template>

                <!-- ============ Platform x status table ============ -->
                <!-- spec 2026-10-07 §10.1 / §10.3 (replaces the round-1 el-tabs).
                     One request per platform; a cell click selects
                     (platform, tab) on the client and never calls the API. The
                     "all" row is last and sums the platforms that loaded. No
                     number is drawn while loading or after a failed load. -->
                <div v-loading="loading" class="px-4 pb-3">
                    <div class="table-responsive bo-orders-new-matrix-wrap">
                        <table class="table table-sm bo-orders-new-matrix">
                            <thead>
                                <tr>
                                    <th scope="col" class="bon-matrix-platform">{{ $t("orders_new.col_platform") }}</th>
                                    <th v-for="key in tabKeys" :key="key" scope="col" class="text-center">
                                        {{ $t(tabLabelKey(key)) }}
                                    </th>
                                    <!-- §19 R45: fulfilment stages after the tab columns. -->
                                    <th
                                        v-for="(key, i) in stageKeys"
                                        :key="'stage-' + key"
                                        scope="col"
                                        class="text-center bon-stage-col"
                                        :class="{ 'bon-stage-first': i === 0 }"
                                    >{{ $t(stageLabelKey(key)) }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="prow in matrixRows"
                                    :key="prow.key"
                                    :class="{ 'bon-matrix-total': prow.key === 'all' }"
                                >
                                    <th scope="row" class="bon-matrix-platform">
                                        <button type="button" class="bon-matrix-name" @click="select(prow.key, 'all')">
                                            {{ prow.label }}<template v-if="countsVisible && prow.truncated">*</template>
                                        </button>
                                        <el-tooltip
                                            v-if="countsVisible && prow.failed"
                                            :content="$t('orders_new.platform_load_error')"
                                            placement="top"
                                        >
                                            <i class="fa fa-exclamation-triangle text-warning ml-1"></i>
                                        </el-tooltip>
                                    </th>
                                    <td v-for="cell in prow.tabs" :key="cell.key" class="text-center">
                                        <button
                                            type="button"
                                            class="bo-orders-new-cell"
                                            :class="{ 'is-selected': isSelected(prow.key, cell.key) }"
                                            :aria-pressed="isSelected(prow.key, cell.key) ? 'true' : 'false'"
                                            @click="select(prow.key, cell.key)"
                                        >
                                            <template v-if="!countsVisible">&nbsp;</template>
                                            <template v-else-if="cell.count === null">–</template>
                                            <span v-else :class="{ 'text-muted': cell.count === 0 && !isSelected(prow.key, cell.key) }">{{ formatTabCount(cell.count) }}</span>
                                        </button>
                                    </td>
                                    <!-- §19 R46: a stage cell = its tab + the client-side stage filter. -->
                                    <td
                                        v-for="(cell, i) in stageCellsOf(prow)"
                                        :key="'stage-' + cell.key"
                                        class="text-center bon-stage-col"
                                        :class="{ 'bon-stage-first': i === 0 }"
                                    >
                                        <button
                                            type="button"
                                            class="bo-orders-new-cell"
                                            :class="{ 'is-selected': isStageSelected(prow.key, cell.key) }"
                                            :aria-pressed="isStageSelected(prow.key, cell.key) ? 'true' : 'false'"
                                            :data-testid="'stage-cell-' + cell.key"
                                            @click="selectStage(prow.key, cell.key)"
                                        >
                                            <template v-if="!countsVisible">&nbsp;</template>
                                            <template v-else-if="cell.count === null">–</template>
                                            <span v-else :class="{ 'text-muted': cell.count === 0 && !isStageSelected(prow.key, cell.key) }">{{ formatTabCount(cell.count) }}</span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- ============ Filter toolbar ============ -->
                <!-- §6.4 item 3: filter (n) · period (not closable; hidden while a
                     date chip shows the range, §11.3 R16) ·
                     one closable chip per field in use · reset · found-N. -->
                <div class="px-4 pb-3 d-flex flex-wrap align-items-center bo-orders-new-toolbar">
                    <base-button size="sm" type="secondary" class="mr-2 mb-2" @click="openFilter">
                        <i class="fa fa-sliders-h"></i>
                        {{ $t("orders_new.btn_filter") }}<template v-if="activeChipList.length"> ({{ activeChipList.length }})</template>
                    </base-button>
                    <el-tooltip :disabled="!filters.q" :content="$t('orders_new.date_ignored_hint')" placement="top">
                        <el-tag v-if="periodTagVisible" size="medium" type="info" class="mr-2 mb-2 bo-orders-new-period">
                            <i class="fa fa-calendar-alt"></i>
                            {{ $t("orders_new.period", { from: filters.time_from, to: filters.time_to }) }}
                        </el-tag>
                    </el-tooltip>
                    <el-tag size="medium" class="mr-2 mb-2 bo-orders-new-selection">
                        {{ $t("orders_new.selection", { platform: selectedPlatformLabel, tab: $t(tabLabelKey(tab)) }) }}
                    </el-tag>
                    <!-- §19 R46: the stage filter (part of the selection: "reset filters" keeps it). -->
                    <el-tag
                        v-if="stage"
                        closable
                        size="medium"
                        type="info"
                        class="mr-2 mb-2 bo-orders-new-stage"
                        data-testid="stage-chip"
                        @close="clearStage"
                    >
                        {{ $t("orders_new.f_stage") }}: {{ String($t(stageLabelKey(stage))).replace(/\s*\n\s*/g, "") }}
                    </el-tag>
                    <el-tag
                        v-for="chip in activeChipList"
                        :key="chip.key"
                        closable
                        size="medium"
                        type="info"
                        class="mr-2 mb-2"
                        @close="onRemoveChip(chip.key)"
                    >
                        {{ $t(chip.labelKey) }}: {{ chip.text }}
                    </el-tag>
                    <el-tooltip v-if="activeChipList.length" :content="$t('orders_new.filter_reset')" placement="top">
                        <base-button size="sm" type="secondary" class="mr-2 mb-2" @click="resetFilters">
                            <i class="fa fa-undo"></i>
                        </base-button>
                    </el-tooltip>
                    <span class="text-muted mb-2">{{ $t("orders_new.found_n", { n: tabRows.length }) }}</span>
                    <!-- §11 F2: reopen the last Shopee check (the order link closes the dialog). -->
                    <base-button
                        v-if="censusAllowed && censusResult && !censusVisible"
                        size="sm"
                        type="secondary"
                        class="ml-2 mb-2"
                        data-testid="census-reopen"
                        @click="censusVisible = true"
                    >
                        <i class="fa fa-clipboard-check"></i> {{ $t("shopee_census.reopen") }}
                    </base-button>
                </div>

                <!-- ============ Alerts ============ -->
                <div v-if="urlRangeError" class="px-4 pb-3">
                    <el-alert type="error" :closable="false" :title="$t('orders_new.range_too_long')"></el-alert>
                </div>
                <div v-if="failedNames" class="px-4 pb-3">
                    <el-alert
                        type="warning"
                        :closable="false"
                        :title="$t('orders_new.partial_load', { names: failedNames })"
                    ></el-alert>
                </div>
                <div v-if="truncatedNames" class="px-4 pb-3">
                    <el-alert
                        type="warning"
                        :closable="false"
                        :title="$t('orders_new.truncated_platforms', { names: truncatedNames })"
                    ></el-alert>
                </div>

                <!-- ============ Table ============ -->
                <!-- Columns in OrderTables.vue order, plus a platform column. -->
                <div v-loading="loading">
                    <el-table
                        ref="ordersTable"
                        class="bon-orders-table bon-table"
                        :data="pageRows"
                        row-key="ID"
                        header-row-class-name="thead-light"
                        :empty-text="emptyText"
                        @expand-change="(row, rows) => onExpandChange(row, rows)"
                    >
                        <template slot="empty">
                            <div class="py-3">
                                <div>{{ emptyText }}</div>
                                <base-button
                                    v-if="activeChipList.length"
                                    size="sm"
                                    type="secondary"
                                    class="mt-2"
                                    @click="resetFilters"
                                >
                                    {{ $t("orders_new.filter_reset") }}
                                </base-button>
                            </div>
                        </template>
                        <!-- §14.4 R27: the order detail opens INLINE under the row. The expand column is
                             1px with its arrow hidden (unscoped CSS below); the order number and the ⋮
                             "view" item toggle it through toggleDetail(row). -->
                        <el-table-column type="expand" width="1" class-name="bon-expand-col">
                            <template slot-scope="{ row }">
                                <div class="bon-detail-sticky" :style="detailPanelStyle">
                                    <orders-new-detail
                                        :row="row"
                                        :detail="detailById[row.ID] || null"
                                        :loading="!!detailLoading[row.ID]"
                                        :error="detailError[row.ID] || ''"
                                        :api-base="apiBase"
                                        :roles="roleFlags"
                                        :payment-busy="!!paymentBusy[row.ID]"
                                        @close="closeDetail(row)"
                                        @retry="loadDetail(row)"
                                        @preview="openPreview"
                                        @payment-check="(kind) => checkPayment(row, kind)"
                                        @tracking="openTimeline"
                                        @change-tracking="r => openRowAction('tracking', r)"
                                        :can-edit-recipient-role="recoveryRoles.isFullOrder"
                                        @edit-recipient="openRecipientEdit"
                                    ></orders-new-detail>
                                </div>
                            </template>
                        </el-table-column>
                        <!-- §13 R22/R23: order/platform/tracking/status are fixed `width` (+ bon-tight) so el-table
                             hands the spare width to the min-width columns that used to wrap.
                             §16 R38: order 104 → 96, created min-width 175px → fixed 140 + bon-tight,
                             status 140 → 132 (measured content + 16px padding). -->
                        <el-table-column :label="$t('order_number')" width="96" class-name="bon-tight">
                            <template slot-scope="{ row }">
                                <a href="#" class="font-weight-bold" @click.prevent="toggleDetail(row)">{{ row.ID }}</a>
                                <button
                                    v-if="clipboardText(row.ID)"
                                    type="button"
                                    class="btn btn-link p-0 ml-1 bo-copy"
                                    data-testid="copy-order"
                                    :title="$t('orders_new.copy')"
                                    :aria-label="$t('orders_new.copy')"
                                    @click.stop="copy(row.ID)"
                                ><i class="fa fa-copy"></i></button>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('created')" width="140" class-name="bon-tight">
                            <template slot-scope="{ row }">{{ row.created_at }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('orders_new.col_platform')" width="100" class-name="bon-tight">
                            <template slot-scope="{ row }">{{ platformName(row.platform_id) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('sale_channel')" min-width="160px">
                            <template slot-scope="{ row }">{{ saleChannelName(row.bo_sale_channel_id) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('recipient_name')" min-width="170px">
                            <template slot-scope="{ row }">{{ row.recipient_name }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('price')" min-width="100px" align="right">
                            <template slot-scope="{ row }">{{ formatAmount(row.pay_amount) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('recipient_phone')" min-width="150px">
                            <template slot-scope="{ row }">{{ row.recipient_phone }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('order_user')" min-width="170px">
                            <template slot-scope="{ row }">{{ row.bo_order_user_name }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('tracking_number')" width="170" class-name="bon-tight">
                            <template slot-scope="{ row }">
                                {{ row.shipping_tracking_number }}
                                <button
                                    v-if="clipboardText(row.shipping_tracking_number)"
                                    type="button"
                                    class="btn btn-link p-0 ml-1 bo-copy"
                                    data-testid="copy-tracking"
                                    :title="$t('orders_new.copy')"
                                    :aria-label="$t('orders_new.copy')"
                                    @click.stop="copy(row.shipping_tracking_number)"
                                ><i class="fa fa-copy"></i></button>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('status')" width="132" class-name="bon-tight">
                            <template slot-scope="{ row }">
                                <span class="badge bon-badge-lg" :class="statusBadgeClass(row.order_status)">{{ statusLabel(row.order_status) }}</span>
                                <!-- Shopee cancel reconcile §11 F1: request still open at Shopee / stored Shopee
                                     status of a 140 row (may be stale). Own line under the status. -->
                                <div v-if="statusExtras(row).cancelRequested || statusExtras(row).staleChip" class="bon-status-extras">
                                    <span v-if="statusExtras(row).cancelRequested" class="badge badge-warning" data-testid="status-cancel-requested">{{ $t("orders_new.cancel_requested") }}</span>
                                    <el-tooltip v-if="statusExtras(row).staleChip" :content="$t('orders_new.platform_status_stale')" placement="top">
                                        <span class="badge badge-secondary" data-testid="status-stale-chip">{{ statusExtras(row).staleChip }}</span>
                                    </el-tooltip>
                                </div>
                            </template>
                        </el-table-column>
                        <!-- §12.2: the old page's row buttons as a ⋮ menu (items from
                             rowActions; hidden, never disabled). No el-tooltip around the
                             trigger — it fights the click trigger.
                             §16 R37: fixed="right" — stays at the right edge when the table
                             scrolls sideways (the dropdown popper is appended to <body>). -->
                        <el-table-column :label="$t('actions')" width="64" fixed="right" align="center" class-name="bon-tight">
                            <template slot-scope="{ row }">
                                <el-dropdown trigger="click" placement="bottom-end" @command="cmd => onRowCommand(cmd, row)">
                                    <base-button
                                        size="sm"
                                        type="secondary"
                                        icon
                                        data-testid="row-menu"
                                        :aria-label="$t('orders_new.menu')"
                                        :title="$t('orders_new.menu')"
                                    >
                                        <i class="fa fa-ellipsis-v"></i>
                                    </base-button>
                                    <el-dropdown-menu slot="dropdown">
                                        <el-dropdown-item
                                            v-for="a in rowActions(row, roleFlags)"
                                            :key="a.key"
                                            :command="a.key"
                                            :divided="a.divided"
                                            :class="{ 'text-danger': a.key === 'cancel' }"
                                            :data-testid="'row-action-' + a.key"
                                        ><i :class="a.icon" class="mr-2"></i>{{ $t(a.labelKey) }}</el-dropdown-item>
                                    </el-dropdown-menu>
                                </el-dropdown>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- ============ Pagination (client) ============ -->
                <!-- §10.3: rows per page (10/20/50/100) next to the pager. -->
                <div class="px-4 py-3 d-flex flex-wrap justify-content-end align-items-center">
                    <span class="text-muted mr-2">{{ $t("orders_new.page_size") }}</span>
                    <el-select
                        :value="size"
                        size="mini"
                        style="width: 120px"
                        class="mr-3 bo-orders-new-page-size"
                        @change="onSizeChange"
                    >
                        <el-option
                            v-for="n in pageSizes"
                            :key="n"
                            :label="$t('orders_new.page_size_option', { n })"
                            :value="n"
                        ></el-option>
                    </el-select>
                    <el-pagination
                        background
                        layout="prev, pager, next"
                        :total="tabRows.length"
                        :page-size="size"
                        :current-page="currentPage"
                        @current-change="onPageChange"
                    ></el-pagination>
                </div>
            </card>
        </div>

        <!-- ============ Filter panel (right side) ============ -->
        <!-- §6.5. Element UI 2.4.11 has no el-drawer: an el-dialog pinned to
             the right by its custom-class (CSS at the bottom, unscoped because
             the dialog is appended to <body>). Its own class name — the seller
             page's tests find their panel by theirs. Edits `draft` only. -->
        <el-dialog
            :visible.sync="filterVisible"
            custom-class="bo-orders-new-filter-drawer"
            :title="$t('orders_new.filter_title')"
            :close-on-click-modal="true"
            :append-to-body="true"
            width="420px"
        >
            <div class="bon-filter-fields">
                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_sub_status") }}</div>
                    <el-radio-group v-model="draft.status" class="bon-radios">
                        <el-radio label="">{{ $t("orders_new.all") }}</el-radio>
                        <el-radio v-for="code in subStatusCodes" :key="code" :label="String(code)">
                            {{ statusLabel(code) }}<template v-if="countsVisible"> ({{ formatTabCount(counts.statusCounts[code] || 0) }})</template>
                        </el-radio>
                    </el-radio-group>
                </div>

                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_date") }}</div>
                    <!-- §11.2 R15: type and range on separate lines (side by side they overlapped in the 420px panel). -->
                    <el-select v-model="draft.date_type" class="bon-date-type w-100 mb-2">
                        <el-option
                            v-for="d in dateTypes"
                            :key="d.value"
                            :label="$t(d.labelKey)"
                            :value="d.value"
                        ></el-option>
                    </el-select>
                    <el-date-picker
                        v-model="draftRange"
                        type="daterange"
                        value-format="yyyy-MM-dd"
                        :clearable="false"
                        class="bon-date-range"
                        style="width: 100%"
                    ></el-date-picker>
                    <small v-if="draftRangeError" class="text-danger d-block mt-1">{{ $t("orders_new.range_too_long") }}</small>
                    <small v-else-if="draftKeywordKind" class="text-muted d-block mt-1">{{ $t("orders_new.date_ignored_hint") }}</small>
                </div>

                <!-- Shopee cancel reconcile §11 F2: admin only, platform Shopee or all. Asks
                     Shopee about the date range above (S1); the result opens in its own dialog. -->
                <div v-if="censusAllowed" class="bon-field bon-census-field" data-testid="census-field">
                    <base-button
                        size="sm"
                        outline
                        type="default"
                        :loading="censusLoading"
                        :disabled="censusLoading"
                        data-testid="census-check"
                        @click="runCensus"
                    >
                        <i class="fa fa-search"></i> {{ $t("shopee_census.btn_check") }}
                    </base-button>
                    <small v-if="censusLoading" class="text-muted d-block mt-1" data-testid="census-checking">{{ $t("shopee_census.checking") }}</small>
                    <small v-else class="text-muted d-block mt-1">{{ $t("shopee_census.hint") }}</small>
                </div>

                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_keyword") }}</div>
                    <el-input
                        v-model="draft.q"
                        clearable
                        :maxlength="100"
                        :placeholder="$t('orders_new.f_keyword')"
                        @keyup.enter.native="applyFilter"
                    ></el-input>
                    <el-tag v-if="draftKeywordKind" size="mini" type="info" class="mt-1">
                        {{ $t("orders_new.kw_" + draftKeywordKind) }}
                    </el-tag>
                </div>

                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_user") }}</div>
                    <el-select v-model="draft.bo_user_id" filterable style="width: 100%">
                        <el-option
                            v-for="option in userOptions"
                            :key="option.id"
                            :label="option.name"
                            :value="option.id"
                        ></el-option>
                    </el-select>
                </div>

                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_sale_channel") }}</div>
                    <el-select v-model="draft.bo_sale_channel_id" filterable style="width: 100%">
                        <el-option
                            v-for="option in saleChannelOptions"
                            :key="option.id"
                            :label="option.name"
                            :value="option.id"
                        ></el-option>
                    </el-select>
                </div>

                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_sale_channel_type") }}</div>
                    <el-select v-model="draft.bo_sale_channel_type_id" style="width: 100%">
                        <el-option
                            v-for="option in saleChannelTypeOptions"
                            :key="option.id"
                            :label="option.name"
                            :value="option.id"
                        ></el-option>
                    </el-select>
                </div>

                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_payment_type") }}</div>
                    <el-select v-model="draft.payment_method_type" style="width: 100%">
                        <el-option
                            v-for="option in paymentTypeOptions"
                            :key="option.id"
                            :label="option.name"
                            :value="option.id"
                        ></el-option>
                    </el-select>
                </div>

                <div class="bon-field">
                    <div class="bon-label">{{ $t("orders_new.f_packing") }}</div>
                    <el-radio-group v-model="draft.packing_not_close" class="bon-radios">
                        <el-radio v-for="p in packingOptions" :key="p.value" :label="p.value">
                            {{ $t(p.labelKey) }}
                        </el-radio>
                    </el-radio-group>
                </div>
            </div>
            <div slot="footer" class="d-flex align-items-center">
                <base-button outline type="default" class="mr-auto" @click="clearDraft">
                    {{ $t("orders_new.filter_clear_all") }}
                </base-button>
                <base-button type="secondary" @click="filterVisible = false">
                    {{ $t("orders_new.filter_cancel") }}
                </base-button>
                <base-button type="primary" :disabled="!!draftRangeError" @click="applyFilter">
                    {{ $t("orders_new.filter_apply") }}
                </base-button>
            </div>
        </el-dialog>

        <!-- Spec 2026-10-14 §5.3: recipient edit (⋮ menu / detail failure line). -->
        <recipient-edit-dialog
            :visible.sync="recipientVisible"
            :order="recipientRow"
            mode="staff"
            @saved="onRecipientSaved"
        ></recipient-edit-dialog>

        <!-- Shopee cancel reconcile §11 F2/F3: census result + repair. -->
        <shopee-cancel-census-dialog
            v-if="censusAllowed"
            :visible.sync="censusVisible"
            :result="censusResult"
            :from="censusFrom"
            :to="censusTo"
            @open-order="onCensusOpenOrder"
            @repaired="onCensusRepaired"
        ></shopee-cancel-census-dialog>

        <!-- §20 R47/R48: one page-level tracking timeline modal shared by every open panel
             (appended to body — never clipped by the expanded row or the fixed-right copy).
             Newest event first; the first one is highlighted. -->
        <el-dialog
            :visible="timelineVisible"
            custom-class="bon-tracking-dialog"
            :title="$t('orders_new.tracking_title')"
            :append-to-body="true"
            width="640px"
            @close="closeTimeline"
        >
            <div v-if="timelineRow" data-testid="tracking-dialog">
                <div class="bon-tl-head" data-testid="tracking-head">
                    <div><span class="bon-tl-k">{{ $t("shipping_provider") }}:</span> <span class="font-weight-bold">{{ timelineRow.shipping_provider_name || "-" }}</span></div>
                    <div>
                        <span class="bon-tl-k">{{ $t("tracking_number") }}:</span> <span class="font-weight-bold">{{ timelineTrackingNumber || "-" }}</span>
                        <button
                            v-if="timelineTrackingNumber"
                            type="button"
                            class="btn btn-link p-0 ml-1 bo-copy"
                            data-testid="tracking-copy"
                            :title="$t('orders_new.copy')"
                            :aria-label="$t('orders_new.copy')"
                            @click.stop="copy(timelineTrackingNumber)"
                        ><i class="fa fa-copy"></i></button>
                    </div>
                    <div v-if="timelineUpdatedAt" class="bon-tl-k" data-testid="tracking-updated">
                        {{ $t("orders_new.tracking_updated", { at: timelineUpdatedAt }) }}
                    </div>
                </div>
                <div v-if="timelineIsLoading" class="text-muted py-3" data-testid="tracking-loading">
                    <i class="fa fa-spinner fa-spin mr-1"></i>{{ $t("orders_new.tracking_loading") }}
                </div>
                <div v-else-if="timelineErr" class="alert alert-secondary d-flex align-items-center mb-0" data-testid="tracking-error">
                    <span class="mr-3">{{ timelineErr }}</span>
                    <base-button size="sm" type="primary" class="ml-auto flex-shrink-0 text-nowrap" @click="retryTimeline">
                        <i class="fa fa-redo mr-1"></i>{{ $t("orders_new.detail_retry") }}
                    </base-button>
                </div>
                <div v-else-if="!timelineItems.length" class="text-muted py-3" data-testid="tracking-empty">
                    {{ $t("orders_new.tracking_empty") }}
                </div>
                <ol v-else class="bon-tl" data-testid="tracking-events">
                    <li
                        v-for="ev in timelineItems"
                        :key="ev.key"
                        class="bon-tl-item"
                        :class="{ 'bon-tl-latest': ev.latest }"
                        data-testid="tracking-event"
                    >
                        <div class="bon-tl-time">{{ ev.time || "-" }}</div>
                        <div class="bon-tl-status">
                            {{ ev.status || ev.statusCode || "-" }}
                            <span v-if="ev.latest" class="badge badge-primary ml-1">{{ $t("orders_new.tracking_latest") }}</span>
                        </div>
                        <div v-if="ev.location" class="bon-tl-sub"><i class="fa fa-map-marker-alt mr-1"></i>{{ ev.location }}</div>
                        <div v-if="ev.office" class="bon-tl-sub">
                            <i class="fa fa-building mr-1"></i>{{ ev.office }}<template v-if="ev.officeTel"> · <i class="fa fa-phone mr-1"></i>{{ ev.officeTel }}</template>
                        </div>
                        <div v-if="ev.message" class="bon-tl-sub">{{ ev.message }}</div>
                    </li>
                </ol>
            </div>
        </el-dialog>

        <!-- §15.3 R34: one page-level slip preview shared by every open panel. position: fixed
             at the page level, not inside the expanded row (el-table overflow/transform).
             Closes on the backdrop, the × button and Esc; never navigates. -->
        <div
            v-if="previewSrc"
            class="bon-lightbox"
            role="dialog"
            aria-modal="true"
            data-testid="slip-lightbox"
            @click.self="closePreview"
        >
            <button
                type="button"
                class="btn btn-sm btn-white bon-lightbox-close"
                data-testid="slip-lightbox-close"
                :aria-label="$t('close')"
                @click="closePreview"
            >&times;</button>
            <img :src="previewSrc" :alt="$t('orders_new.payment_proof')" class="bon-lightbox-img" />
        </div>
    </div>
</template>

<script>
// /backoffice/orders-overview (ภาพรวมรายการสั่งซื้อ) — MadameFin staff only
// (#441; the old /backoffice/orders-new redirects here).
// Data = GET /api/v1/orders, the same as /backoffice/orders;
// layout = /seller/orders. Spec: docs/specs/2026-10-07-bo-orders-new-status-tabs-design.md.
//
// Calls OrderService.ListOrder directly and keeps the rows in this component:
// the Vuex order list action writes the state OrderTables.vue reads, and both
// pages are kept alive at once (spec §2 item 6). Every rule lives in
// src/utils/ordersNew.js (pinned by tests/unit/ordersNew.spec.js).
//
// Round 2 (§10): one request per platform of Order/getPlatformTable, in
// parallel, never bo_platform_id=0 (ruling R8). The panel has no platform
// field any more (R10): `filters.bo_platform_id` stays 0 and each request sets
// its own id through platformApiParams. Selection = { platform, tab } in the
// URL; a legacy ?bo_platform_id=N becomes platform=N.
import {
    Table,
    TableColumn,
    DatePicker,
    Select,
    Option,
    Input,
    Dialog,
    Pagination,
    Alert,
    Tag,
    Tooltip,
    Radio,
    RadioGroup,
    Dropdown,
    DropdownMenu,
    DropdownItem
} from "element-ui";
import moment from "moment";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { OrderService, ShopeeService } from "@/resource";
import { apiErrorMessage } from "@/util/api-error";
import refreshOnArrival from "@/mixins/refreshOnArrival";
import { consumeRefresh, requestRefresh } from "@/utils/pageRefresh";
import { clipboardText, copyToClipboard } from "@/utils/clipboard";
import { formatTabCount } from "@/utils/sellerOrderFilters";
import { canCheckPayment, paymentCheckCall } from "@/utils/orderDetailPanel";
import { formatTimelineTs, timelineRows } from "@/utils/trackingTimeline";
import OrdersNewDetail from "./OrdersNewDetail.vue";
import RecipientEditDialog from "@/components/Packing/RecipientEditDialog.vue";
import { normalizeRevertResult, recoveryActions, revertErrorKey } from "@/utils/trackingFailure";
import { packingRoundErrorKey } from "@/utils/sellerPackingRound";
import ShopeeCancelCensusDialog from "./ShopeeCancelCensusDialog.vue";
import { CENSUS_MAX_DAYS, censusParams, censusRangeError, rangeDays } from "@/utils/shopeeCancelCensus";
import {
    TAB_KEYS,
    DEFAULT_TAB,
    DEFAULT_PLATFORM,
    STATUS_LABEL_KEYS,
    PAGE_SIZE,
    PAGE_SIZES,
    DATE_TYPES,
    PACKING_OPTIONS,
    activeChips,
    apiParams,
    classifyKeyword,
    countTabs,
    emptyFilters,
    filtersFromQuery,
    isTabKey,
    lastPage,
    normalizeFilters,
    normalizePlatform,
    normalizeSize,
    pageOf,
    platformApiParams,
    platformIds,
    platformRows,
    queryFromFilters,
    rangeError,
    removeChip,
    rowActions,
    rowsFor,
    rowsOfPlatform,
    rowsForStage,
    STAGE_KEYS,
    STAGE_STATUS,
    STAGE_TAB,
    isStageKey,
    showPeriodTag,
    statusBadgeClass,
    orderStatusExtras,
    subStatusOptions
} from "@/utils/ordersNew";

const ROUTE_NAME = "BOOrdersOverview";
// §16 R37: width of the fixed-right ⋮ column (template `width="64"`).
const FIXED_RIGHT_WIDTH = 64;

function todayStr() {
    return moment().format("YYYY-MM-DD");
}

/** Store "-- all --" rows carry hardcoded text; relabel id 0 through i18n. */
function withAll(list, allLabel) {
    const rest = (Array.isArray(list) ? list : []).filter(x => x && Number(x.id) !== 0);
    return [{ id: 0, name: allLabel }].concat(rest.map(x => ({ id: Number(x.id), name: x.name })));
}

export default {
    name: "BOOrdersOverview",
    mixins: [refreshOnArrival],
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Input.name]: Input,
        [Dialog.name]: Dialog,
        [Pagination.name]: Pagination,
        [Alert.name]: Alert,
        [Tag.name]: Tag,
        [Tooltip.name]: Tooltip,
        [Radio.name]: Radio,
        [RadioGroup.name]: RadioGroup,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        OrdersNewDetail,
        RecipientEditDialog,
        ShopeeCancelCensusDialog
    },
    data() {
        const today = todayStr();
        return {
            recipientVisible: false,
            recipientRow: null,
            loading: false,
            // True once at least one platform loaded: counts are hidden otherwise (edge 6).
            loaded: false,
            // §10.1: bumped before every batch; a batch whose seq is stale is dropped.
            fetchSeq: 0,
            // Selection (§10.2): platform 'all' | '<id>' x tab.
            platform: DEFAULT_PLATFORM,
            tab: DEFAULT_TAB,
            tabKeys: TAB_KEYS,
            // §19 R46: '' | one of STAGE_KEYS — narrows the tab on the client.
            stage: "",
            // Owner 2026-10-08: the in_transit stage column duplicated the
            // "กำลังจัดส่ง" tab column — the table shows the other four only.
            stageKeys: STAGE_KEYS.filter(k => k !== "in_transit"),
            filters: emptyFilters(today),
            // The panel's working copy — "cancel" throws it away.
            draft: emptyFilters(today),
            filterVisible: false,
            page: 1,
            size: PAGE_SIZE,
            pageSizes: PAGE_SIZES,
            // { "<id>": DBOrder[] | null } — every row of the window per platform,
            // all statuses (null = that request failed). Cells/pages are cut from it.
            rowsByPlatform: {},
            dateTypes: DATE_TYPES,
            packingOptions: PACKING_OPTIONS,
            // §14.4 R28: inline detail, fetched once per row per search().
            detailById: {},
            detailLoading: {},
            detailError: {},
            // §15.3 R34: the slip shown in the page-level preview ("" = closed).
            previewSrc: "",
            // body.style.overflow before the preview locked the page scroll.
            previewBodyOverflow: "",
            // §15.3 R35: { "<ID>": true } while that row's payment check runs.
            paymentBusy: {},
            // §20 R47: the row whose tracking timeline modal is open (null = closed) and
            // the per-order cache (kept while on the page; cleared when it is left).
            timelineRow: null,
            timelineById: {},
            timelineLoading: {},
            timelineError: {},
            apiBase: process.env.VUE_APP_API_BASE_URL || "",
            // Visible width of the table: the panel sticks to it while the table scrolls sideways.
            tableViewWidth: 0,
            // Shopee cancel reconcile §11 F2: S1 in flight / its last answer / the
            // window it ran on / the result dialog.
            censusLoading: false,
            censusResult: null,
            censusFrom: "",
            censusTo: "",
            censusVisible: false
        };
    },
    computed: {
        // §20 R47/R48: the tracking timeline modal (state per order id).
        timelineVisible() {
            return !!this.timelineRow;
        },
        timelineDatum() {
            return this.timelineRow ? this.timelineById[this.timelineRow.ID] || null : null;
        },
        timelineIsLoading() {
            return !!(this.timelineRow && this.timelineLoading[this.timelineRow.ID]);
        },
        timelineErr() {
            return (this.timelineRow && this.timelineError[this.timelineRow.ID]) || "";
        },
        // Newest first (util: sortTimelineDesc); the first row is the highlighted one.
        timelineItems() {
            return timelineRows(this.timelineDatum);
        },
        timelineTrackingNumber() {
            const d = this.timelineDatum;
            return clipboardText(d && d.tracking_number) || clipboardText(this.timelineRow && this.timelineRow.shipping_tracking_number);
        },
        timelineUpdatedAt() {
            const d = this.timelineDatum;
            return d ? formatTimelineTs(d.last_updated_at) : "";
        },
        // §12.1: same getter names as OrderTables.vue (its row-button rules).
        ...mapGetters({
            isAdmin: "Auth/isAdmin",
            isOwner: "Auth/isOwner",
            isEmployee: "Auth/isEmployee",
            isAgent: "Auth/isAgent",
            isAccessPayment: "Auth/isAccessPayment"
        }),
        roleFlags() {
            return {
                isAdmin: !!this.isAdmin,
                isOwner: !!this.isOwner,
                isEmployee: !!this.isEmployee,
                isAccessPayment: !!this.isAccessPayment,
                isAgent: !!this.isAgent
            };
        },
        // Spec 2026-10-14 §5.3 — the recovery items' own gates (kept out of
        // roleFlags, whose shape the round-4 menu pins).
        recoveryRoles() {
            const g = (this.$store && this.$store.getters) || {};
            return { isFullOrder: !!g["Auth/isFullOrder"], isFullPayment: !!g["Auth/isFullPayment"] };
        },
        platformList() {
            return this.$store.getters["Order/getPlatformTable"] || [];
        },
        // §11 F2: the "ตรวจสอบกับ Shopee" tool — admins, with Shopee (3) or all platforms selected.
        censusAllowed() {
            return !!this.isAdmin && (this.platform === DEFAULT_PLATFORM || this.platform === "3");
        },
        platformIdList() {
            return platformIds(this.platformList);
        },
        matrixRows() {
            return platformRows(this.rowsByPlatform, this.platformList, key => this.$t(key));
        },
        selectedRows() {
            return rowsOfPlatform(this.rowsByPlatform, this.platform);
        },
        // Sub-status counts in the panel = the selected platform row.
        counts() {
            return countTabs(this.selectedRows);
        },
        countsVisible() {
            return this.loaded && !this.loading;
        },
        tabRows() {
            return rowsForStage(rowsFor(this.selectedRows, this.tab, this.filters.status), this.stage);
        },
        currentPage() {
            return Math.min(Math.max(1, this.page), lastPage(this.tabRows.length, this.size));
        },
        pageRows() {
            return pageOf(this.tabRows, this.currentPage, this.size);
        },
        selectedPlatformLabel() {
            const hit = this.matrixRows.find(r => r.key === this.platform);
            return hit ? hit.label : this.$t("orders_new.platform_all");
        },
        // §10.1: one alert naming the platforms that failed / hit the row cap.
        failedNames() {
            if (!this.countsVisible) return "";
            return this.platformRowNames(r => r.failed);
        },
        truncatedNames() {
            if (!this.countsVisible) return "";
            return this.platformRowNames(r => r.truncated);
        },
        // A window from the URL the BE would reject: not sent, said instead.
        urlRangeError() {
            return rangeError(this.filters);
        },
        subStatusCodes() {
            return subStatusOptions(this.tab);
        },
        draftRangeError() {
            return rangeError(this.draft);
        },
        draftKeywordKind() {
            return classifyKeyword(this.draft.q, this.draft.bo_platform_id).kind;
        },
        draftRange: {
            get() {
                return this.draft.time_from || this.draft.time_to ? [this.draft.time_from, this.draft.time_to] : null;
            },
            set(value) {
                const v = Array.isArray(value) ? value : [];
                this.draft.time_from = v[0] || "";
                this.draft.time_to = v[1] || "";
            }
        },
        userOptions() {
            const users = this.$store.getters["User/getData"] || [];
            return [{ id: 0, name: this.$t("orders_new.all") }].concat(
                users.map(u => ({ id: Number(u.UserID), name: `${u.fname || ""} ${u.lname || ""}`.trim() }))
            );
        },
        saleChannelOptions() {
            return withAll(this.$store.getters["SaleChannel/getDataWithAll"], this.$t("orders_new.all"));
        },
        saleChannelTypeOptions() {
            return withAll(this.$store.getters["SaleChannel/getDataTypeWithAll"], this.$t("orders_new.all"));
        },
        paymentTypeOptions() {
            return withAll(
                this.$store.getters["PaymentMethod/getPaymentMethodTypeTableWithAll"],
                this.$t("orders_new.all")
            );
        },
        activeChipList() {
            const nameIn = list => id => {
                const hit = list.find(x => x.id === Number(id));
                return hit ? hit.name : "";
            };
            return activeChips(
                this.filters,
                {
                    t: key => this.$t(key),
                    user: nameIn(this.userOptions),
                    saleChannel: nameIn(this.saleChannelOptions),
                    saleChannelType: nameIn(this.saleChannelTypeOptions),
                    paymentType: nameIn(this.paymentTypeOptions)
                },
                todayStr()
            );
        },
        periodTagVisible() {
            return showPeriodTag(this.filters, todayStr());
        },
        // §16 R37: el-table 2.4 overlays the fixed-right ⋮ column (and its own
        // copy of every expanded row) on the right edge, so the panel ends
        // where that column begins instead of running under it.
        detailPanelStyle() {
            const width = this.tableViewWidth - FIXED_RIGHT_WIDTH;
            return this.tableViewWidth && width > 0 ? { width: width + "px" } : null;
        },
        emptyText() {
            return this.activeChipList.length ? this.$t("orders_new.empty_filtered") : this.$t("orders_new.empty_tab");
        }
    },
    watch: {
        // Same path, new query (browser back, a pasted URL): re-read the URL
        // unless it is the state we just wrote; refetch only when the API
        // params changed — tab / sub-status / page are client-side. A kept-alive
        // instance also sees other pages' routes: ignore those.
        "$route.query"(query) {
            if (!this.$route || this.$route.name !== ROUTE_NAME) return;
            const today = todayStr();
            const want = JSON.stringify(this.currentQuery(today));
            const parsed = this.stateFromQuery(query);
            const got = JSON.stringify(
                queryFromFilters(parsed.tab, parsed.filters, parsed.page, today, {
                    platform: parsed.platform,
                    size: parsed.size,
                    stage: parsed.stage
                })
            );
            if (want === got && !parsed.migrated) return;
            const before = JSON.stringify(apiParams(this.filters));
            this.restoreFromQuery(query);
            if (JSON.stringify(apiParams(this.filters)) !== before) this.search();
        }
    },
    created() {
        this.restoreFromQuery(this.$route.query);
    },
    mounted() {
        // This mount loads anyway: drop a pending refresh flag so the next
        // activation does not load a second time (refreshOnArrival mixin).
        consumeRefresh(this.$route.fullPath);
        this.$store.dispatch("User/listData");
        this.$store.dispatch("SaleChannel/listData");
        this.$store.dispatch("PaymentMethod/listData");
        // §19 R44: storage names for "สายที่แพ็ค" in the detail (OrderTables.vue loads the same).
        this.$store.dispatch("WmsStorage/listData");
        this.search();
        window.addEventListener("resize", this.measureTable);
    },
    deactivated() {
        // keep-alive: leaving the page must not leave the body scroll locked.
        this.closePreview();
        // §20 R47: the timeline cache lives "while on the page".
        this.closeTimeline();
        this.clearTimelines();
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.measureTable);
        this.closePreview();
    },
    methods: {
        // refreshOnArrival: an arrival with intent (requestRefresh) reloads.
        reloadOnArrival() {
            this.search();
        },
        // URL -> state. The panel has no platform field (R10): bo_platform_id is
        // forced to 0, and a legacy ?bo_platform_id=N (no `platform`) selects
        // row N instead (`migrated` -> the URL is rewritten).
        stateFromQuery(query) {
            const q = query || {};
            const parsed = filtersFromQuery(q, todayStr());
            const legacy = parsed.filters.bo_platform_id;
            const hasPlatform = q.platform !== undefined && q.platform !== null && q.platform !== "";
            const raw = !hasPlatform && legacy ? legacy : parsed.platform;
            return {
                platform: normalizePlatform(raw, this.platformIdList),
                tab: parsed.tab,
                stage: parsed.stage,
                filters: Object.assign({}, parsed.filters, { bo_platform_id: 0 }),
                page: parsed.page,
                size: parsed.size,
                migrated: !!legacy
            };
        },
        restoreFromQuery(query) {
            const st = this.stateFromQuery(query);
            this.platform = st.platform;
            this.tab = st.tab;
            this.stage = st.stage;
            this.filters = st.filters;
            this.page = st.page;
            this.size = st.size;
            if (st.migrated) this.syncQuery();
        },
        currentQuery(today) {
            return queryFromFilters(this.tab, this.filters, this.page, today || todayStr(), {
                platform: this.platform,
                size: this.size,
                stage: this.stage
            });
        },
        syncQuery() {
            this.$router.replace({ query: this.currentQuery() }).catch(() => {});
        },
        // §10.1: one OrderService.ListOrder per platform, in parallel. A failed
        // platform resolves to rows: null (never rejects the batch); only when
        // every platform fails is it an error dialog (edge 6).
        async search() {
            if (rangeError(this.filters)) {
                // Would be a 400 ERROR_INVALID_DATE_RANGE; urlRangeError says why.
                this.fetchSeq += 1;
                this.rowsByPlatform = {};
                this.loaded = false;
                this.loading = false;
                return;
            }
            this.fetchSeq += 1;
            const seq = this.fetchSeq;
            const filters = this.filters;
            // §14.4 R28: a new list means the cached details may be stale.
            this.clearDetails();
            const ids = this.platformIdList;
            this.loading = true;
            const results = await Promise.all(
                ids.map(id =>
                    Promise.resolve()
                        .then(() => OrderService.ListOrder(platformApiParams(filters, id)))
                        .then(
                            res => ({ id, rows: res && Array.isArray(res.data) ? res.data : [] }),
                            err => ({ id, rows: null, err })
                        )
                )
            );
            if (seq !== this.fetchSeq) return; // a newer batch owns the table
            const next = {};
            results.forEach(r => {
                next[String(r.id)] = r.rows;
            });
            this.rowsByPlatform = next;
            this.loading = false;
            const failed = results.filter(r => r.rows === null);
            this.loaded = !results.length || failed.length < results.length;
            if (!this.loaded) {
                swal.fire({
                    icon: "error",
                    title: this.$t("orders_new.load_error"),
                    text: apiErrorMessage(failed[0].err, (k) => this.$t(k))
                });
            }
        },
        // A cell (or a platform name = its ทั้งหมด cell). Client-side only.
        // A tab cell clears the stage filter (§19 R46).
        select(platform, tab) {
            this.platform = normalizePlatform(platform, this.platformIdList);
            this.tab = isTabKey(tab) ? tab : DEFAULT_TAB;
            this.stage = "";
            // A sub-status outside the new tab means nothing there.
            if (this.filters.status && subStatusOptions(this.tab).indexOf(Number(this.filters.status)) < 0) {
                this.filters = Object.assign({}, this.filters, { status: "" });
            }
            this.page = 1;
            this.syncQuery();
        },
        isSelected(platform, tab) {
            return this.platform === platform && this.tab === tab && !this.stage;
        },
        // §19 R46: a stage cell = (platform, STAGE_TAB[stage]) + stage. A sub-status
        // other than the stage's own status would empty the list, so it is dropped.
        selectStage(platform, stage) {
            if (!isStageKey(stage)) return;
            this.platform = normalizePlatform(platform, this.platformIdList);
            this.tab = STAGE_TAB[stage];
            this.stage = stage;
            if (this.filters.status && Number(this.filters.status) !== STAGE_STATUS[stage]) {
                this.filters = Object.assign({}, this.filters, { status: "" });
            }
            this.page = 1;
            this.syncQuery();
        },
        isStageSelected(platform, stage) {
            return this.platform === platform && this.stage === stage;
        },
        // The stage chip's ×: back to the whole tab; no API call.
        clearStage() {
            this.stage = "";
            this.page = 1;
            this.syncQuery();
        },
        platformRowNames(pred) {
            return this.matrixRows
                .filter(r => r.key !== DEFAULT_PLATFORM && pred(r))
                .map(r => r.label)
                .join(", ");
        },
        onSizeChange(n) {
            this.size = normalizeSize(n);
            this.page = 1;
            this.syncQuery();
        },
        onPageChange(p) {
            this.page = p;
            this.syncQuery();
        },
        openFilter() {
            this.draft = Object.assign({}, this.filters);
            this.filterVisible = true;
        },
        clearDraft() {
            this.draft = emptyFilters(todayStr());
        },
        applyFilter() {
            if (this.draftRangeError) return;
            // R10: no platform field — the rows pick the platform.
            this.filters = Object.assign(normalizeFilters(this.draft, todayStr()), { bo_platform_id: 0 });
            // A sub-status from the panel that contradicts the selected stage would
            // show 0 rows under a highlighted cell — drop the stage (mirror of selectStage).
            if (this.stage && this.filters.status && Number(this.filters.status) !== STAGE_STATUS[this.stage]) {
                this.stage = "";
            }
            this.page = 1;
            this.filterVisible = false;
            this.syncQuery();
            this.search();
        },
        onRemoveChip(key) {
            this.filters = removeChip(this.filters, key, todayStr());
            this.page = 1;
            this.syncQuery();
            // The sub-status is applied on the client; everything else is an API param.
            if (key !== "status") this.search();
        },
        // Clears every filter (window back to default) but keeps the selection.
        resetFilters() {
            this.filters = emptyFilters(todayStr());
            this.page = 1;
            this.syncQuery();
            this.search();
        },
        // §14.4 R27 (replaces ruling R5's push to the old page): the order
        // number and the ⋮ "view" item open/close the detail under the row.
        // Several rows may be open at once.
        toggleDetail(row) {
            const table = this.$refs.ordersTable;
            if (table) table.toggleRowExpansion(row);
        },
        closeDetail(row) {
            const table = this.$refs.ordersTable;
            if (table) table.toggleRowExpansion(row, false);
        },
        // el-table emits (row, expandedRows) after every toggle (arrow, API).
        onExpandChange(row, rows) {
            const open = Array.isArray(rows) && rows.indexOf(row) > -1;
            if (!open || !row) return;
            this.measureTable();
            if (this.detailById[row.ID] || this.detailLoading[row.ID]) return;
            this.loadDetail(row);
        },
        // §14.4 R28: GET /api/v1/orders/detail once per row; a failure keeps
        // the panel open with a retry button.
        async loadDetail(row) {
            const id = row.ID;
            const seq = this.fetchSeq;
            this.$set(this.detailLoading, id, true);
            this.$set(this.detailError, id, "");
            try {
                const resp = await OrderService.ListOrderDetail({ id });
                if (seq !== this.fetchSeq) return; // the list was reloaded meanwhile
                const datum = resp && resp.data ? resp.data.Datum : null;
                this.$set(this.detailById, id, datum && typeof datum === "object" ? datum : {});
            } catch (err) {
                if (seq !== this.fetchSeq) return;
                const msg = apiErrorMessage(err, (k) => this.$t(k));
                this.$set(this.detailError, id, msg || this.$t("orders_new.load_error"));
                this.$notify({ type: "danger", message: msg || this.$t("orders_new.load_error") });
            } finally {
                if (seq === this.fetchSeq) this.$set(this.detailLoading, id, false);
            }
        },
        clearDetails() {
            this.detailById = {};
            this.detailLoading = {};
            this.detailError = {};
        },
        // §11 F2 (Shopee cancel reconcile): S1 for the panel's date range. The
        // window is checked here first — > 35 days (or none) is said, not sent.
        // Slow (~15 s / 7 days): the button shows loading until it answers.
        runCensus() {
            if (!this.censusAllowed || this.censusLoading) return Promise.resolve(false);
            const from = this.draft.time_from;
            const to = this.draft.time_to;
            const bad = censusRangeError(from, to);
            if (bad) {
                swal.fire(
                    bad === "too_long"
                        ? {
                            icon: "warning",
                            title: this.$t("shopee_census.range_too_long_title"),
                            text: this.$t("shopee_census.range_too_long_text", { max: CENSUS_MAX_DAYS, days: rangeDays(from, to) })
                        }
                        : { icon: "warning", title: this.$t("shopee_census.range_missing") }
                );
                return Promise.resolve(false);
            }
            this.censusLoading = true;
            return Promise.resolve()
                .then(() => ShopeeService.CancelCensus(censusParams(from, to)))
                .then(
                    (res) => {
                        this.censusLoading = false;
                        this.censusResult = (res && res.data) || { total: 0, summary: {}, orders: [] };
                        this.censusFrom = from;
                        this.censusTo = to;
                        this.filterVisible = false;
                        this.censusVisible = true;
                        return true;
                    },
                    (err) => {
                        this.censusLoading = false;
                        swal.fire({
                            icon: "error",
                            title: this.$t("shopee_census.load_error"),
                            text: apiErrorMessage(err, (k) => this.$t(k))
                        });
                        return false;
                    }
                );
        },
        // Order number in the census table: open that row's inline detail when
        // the row is on the current page (the dialog closes so it can be seen;
        // the toolbar button reopens it).
        onCensusOpenOrder(boId) {
            const row = this.pageRows.find(r => Number(r && r.ID) === Number(boId));
            if (!row) {
                this.$notify({ type: "info", message: this.$t("shopee_census.not_on_page", { id: boId }) });
                return false;
            }
            this.censusVisible = false;
            // expanded = true: opens it, never closes an already open one.
            const table = this.$refs.ordersTable;
            if (table) table.toggleRowExpansion(row, true);
            return true;
        },
        // §11 F3: a real repair changed statuses — reload the list.
        onCensusRepaired() {
            this.search();
        },
        // §15.3 R34: page-level slip preview. Locks the body scroll and listens
        // for Esc only while open; every close path goes through closePreview().
        openPreview(src) {
            if (!src) return;
            if (!this.previewSrc) {
                this.previewBodyOverflow = document.body.style.overflow;
                document.body.style.overflow = "hidden";
                document.addEventListener("keydown", this.onPreviewKey);
            }
            this.previewSrc = src;
        },
        closePreview() {
            if (!this.previewSrc) return;
            this.previewSrc = "";
            document.body.style.overflow = this.previewBodyOverflow || "";
            this.previewBodyOverflow = "";
            document.removeEventListener("keydown", this.onPreviewKey);
        },
        onPreviewKey(e) {
            if (e && (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27)) this.closePreview();
        },
        // §20 R47: the panel's "view tracking" button. Opens the page-level modal and
        // fetches GET /orders/:id/tracking-timeline only the first time per order.
        openTimeline(row) {
            if (!row || !row.ID || !clipboardText(row.shipping_tracking_number)) return;
            this.timelineRow = row;
            this.loadTimeline(row);
        },
        closeTimeline() {
            this.timelineRow = null;
        },
        retryTimeline() {
            if (this.timelineRow) this.loadTimeline(this.timelineRow);
        },
        clearTimelines() {
            this.timelineById = {};
            this.timelineLoading = {};
            this.timelineError = {};
        },
        // Cached answers are reused; a failed load is not cached (reopen / retry asks again).
        async loadTimeline(row) {
            const id = row.ID;
            if (this.timelineById[id] || this.timelineLoading[id]) return;
            this.$set(this.timelineLoading, id, true);
            this.$set(this.timelineError, id, "");
            try {
                const resp = await OrderService.OrderTrackingTimeline(id);
                const datum = resp && resp.data ? resp.data.Datum : null;
                this.$set(this.timelineById, id, datum && typeof datum === "object" ? datum : { timeline: [] });
            } catch (err) {
                // The i18n line always shows; the API's own reason (localized when known) follows.
                const msg = apiErrorMessage(err, (k) => this.$t(k));
                const base = this.$t("orders_new.tracking_error");
                this.$set(this.timelineError, id, msg ? `${base} (${msg})` : base);
            } finally {
                this.$set(this.timelineLoading, id, false);
            }
        },
        // §15.3 R35: payment correct ('success') / incorrect ('fail') from the
        // panel. Always confirms first; one request at a time per row; the list
        // reloads on success (the row changes tab).
        checkPayment(row, kind) {
            const method = paymentCheckCall(kind);
            if (!row || !method || !canCheckPayment(row, this.roleFlags) || this.paymentBusy[row.ID]) {
                return Promise.resolve(false);
            }
            const ok = kind === "success";
            return swal.fire({
                title: this.$t(ok ? "orders_new.confirm_payment_ok" : "orders_new.confirm_payment_fail"),
                text: `#${row.ID} · ${this.$t("orders_new.amount_pay")} ${this.formatAmount(row.pay_amount)}`,
                icon: "warning",
                showCancelButton: true,
                reverseButtons: true,
                confirmButtonText: this.$t(ok ? "payment_success" : "payment_fail"),
                cancelButtonText: this.$t("cancel"),
                confirmButtonClass: ok ? "btn btn-success btn-fill" : "btn btn-danger btn-fill",
                cancelButtonClass: "btn btn-secondary btn-fill",
                buttonsStyling: false
            }).then(result => {
                if (!result || !result.value || this.paymentBusy[row.ID]) return false;
                const id = row.ID;
                this.$set(this.paymentBusy, id, true);
                return Promise.resolve()
                    .then(() => OrderService[method](id, { bo_payment_method_id: row.bo_payment_method_id }))
                    .then(
                        () => {
                            this.$set(this.paymentBusy, id, false);
                            swal.fire({
                                title: this.$t("update_success"),
                                icon: "success",
                                confirmButtonClass: "btn btn-success btn-fill",
                                buttonsStyling: false
                            });
                            this.search();
                            return true;
                        },
                        err => {
                            this.$set(this.paymentBusy, id, false);
                            swal.fire({
                                title: apiErrorMessage(err, (k) => this.$t(k)),
                                icon: "error",
                                confirmButtonClass: "btn btn-danger btn-fill",
                                buttonsStyling: false
                            });
                            return false;
                        }
                    );
            });
        },
        measureTable() {
            const table = this.$refs.ordersTable;
            const el = table && table.$el;
            this.tableViewWidth = el && el.clientWidth ? el.clientWidth : 0;
        },
        // §12.1 / §12.2: the ⋮ menu.
        // The round-4 items + (spec 2026-10-14) recipient edit / stuck-112
        // revert after a divider; the latter use their own gates (recoveryRoles).
        rowActions(row, roles) {
            const extra = recoveryActions(row, this.recoveryRoles).map((a, i) => Object.assign({}, a, { divided: i === 0 }));
            return rowActions(row, roles).concat(extra);
        },
        openRecipientEdit(row) {
            this.recipientRow = row;
            this.recipientVisible = true;
        },
        onRecipientSaved({ id, form }) {
            // Patch the visible row; the detail cache is dropped so the panel re-reads.
            const row = (this.pageRows || []).find(r => Number(r.ID) === Number(id));
            if (row) Object.assign(row, form);
            if (this.detailById && this.detailById[id]) this.$delete(this.detailById, id);
        },
        async revertAwaiting(row) {
            const confirmed = await swal.fire({
                icon: "warning",
                title: this.$t("tracking_failure.revert"),
                text: this.$t("tracking_failure.revert_confirm", { n: 1 }),
                showCancelButton: true,
                confirmButtonText: this.$t("confirm"),
                cancelButtonText: this.$t("cancel")
            });
            if (!confirmed || !confirmed.value) return;
            try {
                const res = await OrderService.revertAwaitingShipment({ order_ids: [Number(row.ID)] });
                const out = normalizeRevertResult(res);
                this.$notify({
                    type: out.reverted > 0 ? "success" : "warning",
                    message: this.$t("tracking_failure.revert_done", { n: out.reverted, total: out.requested || 1 })
                });
                if (out.reverted > 0) row.order_status = 111;
            } catch (err) {
                const key = revertErrorKey(packingRoundErrorKey(err));
                swal.fire({
                    icon: "error",
                    title: this.$t("tracking_failure.revert"),
                    text: key && this.$te(key) ? this.$t(key) : ((err && err.message) || "")
                });
            }
        },
        onRowCommand(cmd, row) {
            if (cmd === "edit_recipient") return this.openRecipientEdit(row);
            if (cmd === "revert_awaiting") return this.revertAwaiting(row);
            if (cmd === "view") this.toggleDetail(row);
            else if (cmd === "shipping" || cmd === "tracking" || cmd === "cancel") this.openRowAction(cmd, row);
            else if (cmd === "sim_delivered") this.simulate(row, "delivered");
            else if (cmd === "sim_failed") this.simulate(row, "failed");
            else if (cmd === "sim_returned") this.simulate(row, "returned");
        },
        // Ruling R18/R19: the shipping / tracking / cancel modals live in
        // OrderTables.vue. Flag both lists for a reload (the old page's
        // activated() needs the flag to reach applyArrival; this page reloads
        // when the user comes back), then hand the row over in params.
        openRowAction(kind, row) {
            requestRefresh("/backoffice/orders");
            requestRefresh("/backoffice/orders-overview");
            this.$router.push({ name: "BOOrders", params: { row_action: { kind, row } } }).catch(() => {});
        },
        simulateActionLabel(action) {
            if (action === "delivered") return this.$t("sim_delivered");
            if (action === "failed") return this.$t("sim_failed");
            return this.$t("sim_returned");
        },
        // Copied from OrderTables.vue#handleSimulate (#189, staging only); runs
        // in place and reloads this list.
        simulate(row, action) {
            return swal.fire({
                title: this.$t("sim_confirm_title"),
                text: this.simulateActionLabel(action) + " — #" + row.ID,
                icon: "warning",
                showCancelButton: true,
                confirmButtonClass: "btn btn-success btn-fill",
                cancelButtonClass: "btn btn-danger btn-fill",
                confirmButtonText: this.$t("yes"),
                cancelButtonText: this.$t("no"),
                buttonsStyling: false
            }).then(result => {
                if (!result || !result.value) return;
                return OrderService.SimulateFulfillment(row.ID, action)
                    .then(() => {
                        swal.fire({
                            title: this.$t("sim_success"),
                            text: this.simulateActionLabel(action),
                            icon: "success",
                            confirmButtonClass: "btn btn-success btn-fill",
                            buttonsStyling: false
                        });
                        this.search();
                    })
                    .catch(err => {
                        const data = err && err.response && err.response.data;
                        swal.fire({
                            title: "Error",
                            text: (data && (data.Msg || data.Message)) || String(err),
                            icon: "error",
                            confirmButtonClass: "btn btn-danger btn-fill",
                            buttonsStyling: false
                        });
                    });
            });
        },
        // §12.4: copy exactly what the cell shows.
        clipboardText(value) {
            return clipboardText(value);
        },
        async copy(value) {
            const text = clipboardText(value);
            const ok = await copyToClipboard(text);
            if (ok) this.$notify({ type: "success", message: this.$t("orders_new.copied", { value: text }) });
            else this.$notify({ type: "danger", message: this.$t("orders_new.copy_failed") });
            return ok;
        },
        tabLabelKey(key) {
            return `orders_new.tab_${key}`;
        },
        // The stage cells the table shows (stageKeys order) — in_transit is
        // counted in platformRows but not shown (owner 2026-10-08).
        stageCellsOf(prow) {
            const cells = prow && Array.isArray(prow.stages) ? prow.stages : [];
            return cells.filter(c => c && this.stageKeys.indexOf(c.key) >= 0);
        },
        stageLabelKey(key) {
            return `orders_new.stage_${key}`;
        },
        formatTabCount(n) {
            return formatTabCount(n);
        },
        statusBadgeClass(code) {
            return statusBadgeClass(code);
        },
        // §11 F1 (Shopee cancel reconcile): { cancelRequested, staleChip }.
        statusExtras(row) {
            return orderStatusExtras(row);
        },
        statusLabel(code) {
            const key = STATUS_LABEL_KEYS[Number(code)];
            return key ? this.$t(key) : String(code);
        },
        platformName(id) {
            const hit = this.platformList.find(x => x.id === Number(id));
            return hit ? hit.name : "-";
        },
        saleChannelName(id) {
            const list = this.$store.getters["SaleChannel/getData"] || [];
            const hit = list.find(x => x.id === id);
            return hit ? hit.name : "-";
        },
        formatAmount(value) {
            const n = Number(value || 0);
            return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
    }
};
</script>

<style>
/* spec 2026-10-07 §6.1 — copied (not shared) from Seller/Orders.vue's
   right-panel CSS under this page's own class: unscoped because the
   dialog is appended to <body>, and the seller page's tests find their panel
   by their class name. */
.bo-orders-new-filter-drawer {
    position: fixed;
    right: 0;
    top: 0;
    margin: 0 !important;
    height: 100vh;
    display: flex;
    flex-direction: column;
    border-radius: 0;
}
.bo-orders-new-filter-drawer .el-dialog__body {
    flex: 1;
    overflow-y: auto;
    padding-top: 8px;
}
.bo-orders-new-filter-drawer .bon-field {
    margin-bottom: 14px;
}
.bo-orders-new-filter-drawer .bon-label {
    font-size: 0.8rem;
    color: #525f7f;
    margin-bottom: 4px;
}
.bo-orders-new-filter-drawer .bon-radios .el-radio {
    display: block;
    margin: 0 0 8px 0;
}
@media (max-width: 575.98px) {
    .bo-orders-new-filter-drawer {
        width: 100% !important;
    }
}
/* Shopee cancel reconcile §11 F1: extra labels on their own line under the
   status badge (table cell and detail header). */
.bon-status-extras {
    margin-top: 4px;
    line-height: 1.6;
}
.bon-status-extras .badge {
    font-weight: 400;
    white-space: normal;
}
.bon-census-field {
    border-top: 1px solid #e9ecef;
    padding-top: 12px;
}
.badge.bon-badge-lg {
    font-size: 0.8rem;
    padding: 0.4em 0.65em;
    font-weight: 400;
}
.bo-orders-new-toolbar .bo-orders-new-period {
    cursor: default;
}
/* §10.3 platform x status table. Narrow screens scroll sideways with the
   platform column pinned. Selected cell = $app_primary (#1c1026) with white
   text (~17:1). */
.bo-orders-new-matrix {
    margin-bottom: 0;
    white-space: nowrap;
}
.bo-orders-new-matrix th,
.bo-orders-new-matrix td {
    vertical-align: middle;
}
.bo-orders-new-matrix td {
    padding: 0.2rem;
}
.bo-orders-new-matrix .bon-matrix-platform {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
    text-align: left;
}
.bo-orders-new-matrix thead .bon-matrix-platform {
    background: #f6f9fc;
}
.bo-orders-new-matrix .bon-matrix-total th,
.bo-orders-new-matrix .bon-matrix-total td {
    border-top: 2px solid #adb5bd;
    font-weight: 700;
}
.bo-orders-new-matrix .bon-matrix-name {
    border: 0;
    background: transparent;
    padding: 0;
    color: #32325d;
    font-weight: inherit;
    cursor: pointer;
}
.bo-orders-new-matrix .bon-matrix-name:hover {
    text-decoration: underline;
}
/* §19 R45 (render-measured, harness 1366 / 1920): 13 count columns did not
   fit — the theme's `.card .table td/th` 1.5rem side padding made every column
   112px (table 1453px in a 1008px card at 1366 → sideways scroll). Same fix as
   bon-tight (§12 R20): small side padding on this table only, at a higher
   specificity than the theme rule. Count headers may wrap to two lines (Thai
   labels) so a column is as wide as its number button, not its label.
   table-layout: fixed = the 12 count columns share the width equally (auto
   layout gave the long-label columns 170px vs 111px at 1920); the number
   button is 60px min here (a 6-char "12,345" needs ~56px) and min-width keeps
   every column >= 60 + 4px (112 + 12 x 64 = 880), the wrapper scrolls below
   that. The five stage columns get a divider before the group. */
.card .table.bo-orders-new-matrix {
    table-layout: fixed;
    min-width: 880px;
}
.card .table.bo-orders-new-matrix thead .bon-matrix-platform {
    width: 112px;
}
.card .table.bo-orders-new-matrix .bo-orders-new-cell {
    min-width: 60px;
}
.card .table.bo-orders-new-matrix td {
    padding-left: 2px;
    padding-right: 2px;
}
.card .table.bo-orders-new-matrix thead th {
    padding-left: 6px;
    padding-right: 6px;
    white-space: normal;
    line-height: 1.3;
    vertical-align: bottom;
    /* The theme's uppercase + 1px letter-spacing split English labels inside
       the word ("COMPLET/ED") at 68-75px columns; Thai is unaffected. */
    text-transform: none;
    letter-spacing: 0;
    overflow-wrap: normal;
    word-break: normal;
}
.card .table.bo-orders-new-matrix .bon-matrix-platform {
    padding-left: 12px;
    padding-right: 12px;
    white-space: nowrap;
}
.bo-orders-new-matrix .bon-stage-first {
    border-left: 2px solid #dee2e6;
}
/* A long Thai stage label carries its own line break ("รอขนส่ง\nเข้ารับ"):
   the browser's Thai word breaking split it as "รอขนส่งเข้า / รับ". */
.card .table.bo-orders-new-matrix thead th.bon-stage-col {
    white-space: pre-line;
}
.bo-orders-new-cell {
    display: block;
    width: 100%;
    min-width: 64px;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    padding: 0.25rem 0.5rem;
    color: #32325d;
    font-weight: inherit;
    cursor: pointer;
}
.bo-orders-new-cell:hover {
    background: #f7fafc;
}
.bo-orders-new-cell:focus {
    outline: 2px solid #1c1026;
    outline-offset: 1px;
}
/* Same specificity as :hover above and later in source, so it wins on hover. */
.bo-orders-new-cell.is-selected {
    background: #1c1026;
    color: #fff;
}
/* §12 R20 (verifier render 2026-10-07): the theme gives every `.card .el-table td`
   1.5rem of side padding, which left 16px of content in the 64px actions column
   (the 29px ⋮ was clipped) and pushed the copy icon to a second line in the
   order / tracking columns. Same approach as the old page (#396): 8px on the
   three tight columns only, via el-table's `class-name`, at a specificity that
   beats the theme rule. */
.card .el-table td.bon-tight,
.card .el-table th.bon-tight {
    padding-left: 8px;
    padding-right: 8px;
}
/* §14.4 R27: the expand column only carries the inline detail — no arrow, no
   padding (the theme's 1.5rem would widen the 1px column). */
.bon-expand-col .cell,
.bon-expand-col .el-table__expand-icon {
    display: none;
}
th.bon-expand-col,
.card .el-table td.bon-expand-col,
.card .el-table th.bon-expand-col {
    padding: 0;
}
/* The expanded cell spans the whole (scrollable) table: no Element 20px 50px /
   theme 1.5rem padding; the panel sticks to the visible part (width set from
   the table's clientWidth) so the slip column never sits off-screen. */
.card .bon-orders-table td.el-table__expanded-cell {
    padding: 0;
}
.bon-orders-table .bon-detail-sticky {
    position: sticky;
    left: 0;
    max-width: 100%;
}
/* The `.bon-table` fixed-right rules (§17 R40 / §18 R41 / §16 R37) moved to
   src/assets/sass/custom/_bon-table.scss, imported once by DashboardLayout.vue (#416 F3). */
/* §20 R47/R48: tracking timeline modal. Appended to body ⇒ unscoped, under its own
   custom-class. 640px, never wider than the screen; the event list scrolls inside. */
.bon-tracking-dialog {
    max-width: calc(100vw - 16px);
}
.bon-tracking-dialog .el-dialog__body {
    padding-top: 8px;
}
.bon-tracking-dialog .bon-tl-head {
    padding-bottom: 10px;
    margin-bottom: 12px;
    border-bottom: 1px solid #e9ecef;
    overflow-wrap: anywhere;
}
.bon-tracking-dialog .bon-tl-k {
    color: #6c757d;
}
.bon-tracking-dialog .bon-tl {
    list-style: none;
    margin: 0;
    padding: 0 0 0 4px;
    /* verifier 10-08: header + padding ≈ 200px; keep the whole dialog inside short viewports */
    max-height: calc(85vh - 200px);
    overflow-y: auto;
}
.bon-tracking-dialog .bon-tl-item {
    position: relative;
    padding: 0 0 14px 22px;
    border-left: 2px solid #e9ecef;
    margin-left: 6px;
    overflow-wrap: anywhere;
}
.bon-tracking-dialog .bon-tl-item:last-child {
    border-left-color: transparent;
    padding-bottom: 0;
}
.bon-tracking-dialog .bon-tl-item::before {
    content: "";
    position: absolute;
    left: -7px;
    top: 4px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #adb5bd;
}
.bon-tracking-dialog .bon-tl-latest::before {
    background: #1c1026;
    border-color: #1c1026;
}
.bon-tracking-dialog .bon-tl-time {
    font-size: 0.8125rem;
    color: #6c757d;
}
.bon-tracking-dialog .bon-tl-status {
    color: #32325d;
}
.bon-tracking-dialog .bon-tl-latest .bon-tl-status {
    font-weight: 700;
}
.bon-tracking-dialog .bon-tl-sub {
    font-size: 0.8125rem;
    color: #525f7f;
}
</style>
<style scoped>
/* §12.4 R21: copy icon always visible. #525f7f ≈ 6.4:1 on white; hover/focus =
   $app_primary (#1c1026). Not text-muted (its !important beats :hover). */
.bo-copy {
    color: #525f7f;
    font-size: 0.8rem;
    vertical-align: baseline;
    line-height: 1;
}
.bo-copy:hover,
.bo-copy:focus {
    color: #1c1026;
}
/* §11.2 R15: el-date-editor--daterange has its own 350px width; the range
   picker spans the panel on its own line. */
.bon-date-range {
    width: 100% !important;
}
/* §15.3 R34: page-level slip preview (above the el-dialog drawer, z 2000+). */
.bon-lightbox {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3000;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}
.bon-lightbox-img {
    max-width: 92vw;
    max-height: 92vh;
    object-fit: contain;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
.bon-lightbox-close {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 1.25rem;
    line-height: 1;
    padding: 4px 10px;
}
</style>
